import { createSlice } from '@reduxjs/toolkit';
import {
  delRelativesOfRelativeAsync,
  getRelativeActionsAsync,
  getRelativeAsync,
  getRelativesOfRelativeAsync,
  putRelationshipsRelativeAsync,
  putRelativeInfoAsync,
} from './asyncActions';
import {
  normalizeDataRelative,
  normalizeDataRelativeAction,
  normalizeDataRelatives,
  updateRelatives,
} from './operations';
import { postNewRelativeAsync } from 'features/relatives/relativesSlice';

export interface RelativeDetailState {
  relativeData: {
    data: null | any;
    status: null | 'idle' | 'loading' | 'failed';
  };
  actions: {
    data: null | any;
    pagination: {
      page: number;
      total: number;
    };
    status: null | 'idle' | 'loading' | 'failed';
  };
  relatives: {
    data: null | any;
    pagination: {
      page: number;
      total: number;
    };
    status: null | 'idle' | 'loading' | 'failed';
  };
  newRelative: any;
  updateRelative: any;
}

const initialState: RelativeDetailState = {
  relativeData: {
    data: null,
    status: null,
  },
  actions: {
    data: [],
    pagination: {
      page: 0,
      total: 0,
    },
    status: null,
  },
  relatives: {
    data: [],
    pagination: {
      page: 0,
      total: 0,
    },
    status: null,
  },
  newRelative: null,
  updateRelative: null,
};

export const relativeDetailSlice = createSlice({
  name: 'relativeDetail',
  initialState,
  reducers: {
    resetRelative: (state) => {
      state.relativeData.data = null;
      state.relativeData.status = null;
      state.actions.data = [];
      state.actions.pagination = {
        page: 0,
        total: 0,
      };
      state.actions.status = null;
      state.relatives.data = [];
      state.relatives.pagination = {
        page: 0,
        total: 0,
      };
      state.relatives.status = null;
      state.newRelative = null;
      state.updateRelative = null;
    },
    resetNewRelative: (state) => {
      state.newRelative = null;
    },
    resetUpdateRelative: (state) => {
      state.updateRelative = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRelativeAsync.pending, (state) => {
        state.relativeData.status = 'loading';
      })
      .addCase(getRelativeAsync.fulfilled, (state, action) => {
        state.relativeData.status = 'idle';
        state.relativeData.data = normalizeDataRelative(action.payload);
        state.actions.data = [];
        state.relatives.data = [];
      })
      .addCase(getRelativeAsync.rejected, (state) => {
        state.relativeData.status = 'failed';
      })
      .addCase(getRelativeActionsAsync.pending, (state) => {
        state.actions.status = 'loading';
      })
      .addCase(getRelativeActionsAsync.fulfilled, (state, action) => {
        state.actions.status = 'idle';
        state.actions.data = normalizeDataRelativeAction(action.payload);
        state.actions.pagination.total = action.payload?.data.item.total;
      })
      .addCase(getRelativeActionsAsync.rejected, (state) => {
        state.actions.status = 'failed';
      })
      .addCase(getRelativesOfRelativeAsync.pending, (state) => {
        state.relatives.status = 'loading';
      })
      .addCase(getRelativesOfRelativeAsync.fulfilled, (state, action) => {
        state.relatives.status = 'idle';
        state.relatives.data = normalizeDataRelatives(action.payload);
        state.relatives.pagination.total = action.payload?.data.item.total;
      })
      .addCase(getRelativesOfRelativeAsync.rejected, (state) => {
        state.relatives.status = 'failed';
      })
      .addCase(putRelativeInfoAsync.fulfilled, (state, action) => {
        state.relativeData.data = action.meta.arg;
        state.updateRelative = action.payload?.data?.success;
      })
      .addCase(delRelativesOfRelativeAsync.fulfilled, (state, action) => {
        state.relatives.data = state.relatives.data.filter((item: any) => item.id !== action.meta.arg.id);
      })
      .addCase(putRelationshipsRelativeAsync.fulfilled, (state, action) => {
        state.relatives.data = updateRelatives(state.relatives.data, action.meta.arg);
      })
      .addCase(postNewRelativeAsync.fulfilled, (state, action) => {
        state.newRelative = action.payload?.data?.success;
      });
  },
});

export const { resetRelative, resetNewRelative, resetUpdateRelative } = relativeDetailSlice.actions;

export default relativeDetailSlice.reducer;
